import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, LogOut, Printer, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import HttpService from '@/shared/services/http.service';
import { API_CONFIG } from '@/shared/constants/api';
import { logEvent } from '@/analytics';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface UserSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NEXT_NUMBERS_PREVIEW_COUNT = 1;

const STICKER_PRINT_STYLES = `
  @page { size: 100mm 50mm; margin: 0; }
  html, body { margin: 0; padding: 0; }
  .print-sticker {
    width: 100mm;
    height: 50mm;
    display: flex;
    align-items: center;
    justify-content: center;
    page-break-after: always;
    break-after: page;
  }
  .print-sticker:last-child { page-break-after: auto; break-after: auto; }
  .print-sticker span {
    font-size: 60pt;
    font-weight: 800;
    font-family: monospace;
    line-height: 1;
    text-align: center;
  }
`;

export const UserSidebar = ({ open, onOpenChange }: UserSidebarProps) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isPrintingRef = useRef(false);
  const [nextPackageNumbers, setNextPackageNumbers] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchNextNumbers = async () => {
      try {
        const response = await HttpService.get(
          API_CONFIG.path.nextPackageNumbers,
          { count: NEXT_NUMBERS_PREVIEW_COUNT },
          { skipNotify: true },
        );
        if (!cancelled) {
          setNextPackageNumbers(Array.isArray(response?.packageNumbers) ? response.packageNumbers : []);
        }
      } catch {
        if (!cancelled) setNextPackageNumbers([]);
      }
    };

    fetchNextNumbers();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!user) return null;

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en');
  };

  const handlePrintStickers = () => {
    if (nextPackageNumbers.length === 0) return;

    logEvent('print_stickers_clicked', { packageCount: nextPackageNumbers.length });

    // Print through an isolated iframe rather than window.print() on the main document —
    // window.print() applies @media print to the whole app, which flashes/hides the visible
    // page the instant it's called. Printing the iframe's own window leaves the app untouched.
    isPrintingRef.current = true;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><style>${STICKER_PRINT_STYLES}</style></head><body>${nextPackageNumbers
          .map((number) => `<div class="print-sticker"><span>${number}</span></div>`)
          .join('')}</body></html>`,
      );
      doc.close();
    }

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };

    let cleaned = false;
    const cleanup = (source: 'afterprint' | 'timeout_fallback') => {
      if (cleaned) return;
      cleaned = true;
      isPrintingRef.current = false;
      document.body.removeChild(iframe);
      logEvent('print_stickers_dialog_closed', { packageCount: nextPackageNumbers.length, source });
    };
    if (iframe.contentWindow) {
      iframe.contentWindow.onafterprint = () => cleanup('afterprint');
    }
    // Fallback in case `afterprint` doesn't fire (some browsers/print-to-PDF flows).
    setTimeout(() => cleanup('timeout_fallback'), 60000);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 w-[85vw] sm:max-w-sm gap-0"
        onPointerDownOutside={(e) => {
          if (isPrintingRef.current) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isPrintingRef.current) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (isPrintingRef.current) e.preventDefault();
        }}
      >
        <SheetTitle className="sr-only">{t('profile')}</SheetTitle>
        {/* Profile header */}
        <div className="bg-gradient-to-br from-primary to-primary/80 px-6 pt-8 pb-6 text-primary-foreground">
          <Avatar className="h-16 w-16 border-2 border-white/30 shadow-lg">
            <AvatarFallback className="bg-white/15 text-white text-xl font-semibold">{user.initials}</AvatarFallback>
          </Avatar>
          <p className="mt-3 text-lg font-semibold leading-tight truncate first-letter:capitalize">{user.name}</p>
          <p className="text-sm text-primary-foreground/80 truncate">{user.email}</p>
          {user.role && (
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="first-letter:capitalize">{user.role}</span>
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {/* Language switch */}
          <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-3 hover:bg-muted/60 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
                <Globe className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{t('language')}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {i18n.language === 'en' ? 'English' : 'Deutsch'}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleLanguage}
              className="h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border border-blue-100 shadow-sm shrink-0 px-3.5 gap-1.5"
            >
              <Globe className="w-3.5 h-3.5" />
              {i18n.language === 'en' ? 'EN' : 'DE'}
            </Button>
          </div>

          {/* Print stickers */}
          <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-3 hover:bg-muted/60 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
                <Printer className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{t('print_sticker')}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {nextPackageNumbers.length > 0 ? nextPackageNumbers.join(', ') : t('loading')}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePrintStickers}
              disabled={nextPackageNumbers.length === 0}
              className="h-8 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 border border-emerald-100 shadow-sm shrink-0 px-3.5 gap-1.5"
            >
              <Printer className="w-3.5 h-3.5" />
              {t('print')}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-4 space-y-3">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              logout();
            }}
            className="w-full justify-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="w-4 h-4" />
            {t('log_out')}
          </Button>

          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <span>{t('powered_by')}</span>
            <img src="/favicon.ico" alt="ScaleTech" className="w-4 h-4 rounded-sm" />
            <span className="font-semibold text-foreground">ScaleTech</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
